/**
 * Utility functions for constructing Firebase Storage URLs
 */

/**
 * Constructs a Firebase Storage URL for a given path
 * @param {string} groupId - The group/organization ID
 * @param {string} path - The path within the storage bucket (e.g., 'welcomeCard/images/0')
 * @returns {string} - Complete Firebase Storage URL
 */
export const buildFirebaseStorageUrl = (groupId, path) => {
  const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;

  if (!bucket) {
    console.warn(
      "VITE_FIREBASE_STORAGE_BUCKET environment variable is not set"
    );
    return "";
  }

  if (!groupId || !path) {
    console.warn("buildFirebaseStorageUrl: groupId and path are required");
    return "";
  }

  // Encode the path components for URL safety
  const encodedPath = `${groupId}%2F${path.replace(/\//g, "%2F")}`;

  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
};

/**
 * Constructs a Firebase Storage URL specifically for welcome card images
 * @param {string} groupId - The group/organization ID
 * @param {number} index - The image index (0 for members, 1 for non-members)
 * @returns {string} - Complete Firebase Storage URL for welcome card image
 */
export const buildWelcomeCardImageUrl = (groupId, index = 0) => {
  return buildFirebaseStorageUrl(groupId, `welcomeCard/images/${index}`);
};

/**
 * Constructs a Firebase Storage URL for partner logos
 * @param {string} groupId - The group/organization ID
 * @param {string} partnerId - The partner ID
 * @returns {string} - Complete Firebase Storage URL for partner logo
 */
export const buildPartnerLogoUrl = (groupId, partnerId) => {
  return buildFirebaseStorageUrl(groupId, `partner/${partnerId}/logo`);
};

/**
 * Constructs a Firebase Storage URL for partner QR images
 * @param {string} groupId - The group/organization ID
 * @param {string} partnerId - The partner ID
 * @returns {string} - Complete Firebase Storage URL for partner QR image
 */
export const buildPartnerQrImageUrl = (groupId, partnerId) => {
  return buildFirebaseStorageUrl(groupId, `partner/${partnerId}/qrImage`);
};

/**
 * Constructs a Firebase Storage URL for partner gallery images
 * @param {string} groupId - The group/organization ID
 * @param {string} partnerId - The partner ID
 * @param {number} imageIndex - The gallery image index
 * @returns {string} - Complete Firebase Storage URL for partner gallery image
 */
export const buildPartnerGalleryImageUrl = (groupId, partnerId, imageIndex) => {
  return buildFirebaseStorageUrl(
    groupId,
    `partner/${partnerId}/gallery/${imageIndex}`
  );
};

/**
 * Base Firebase Storage URL configuration
 */
export const FIREBASE_STORAGE_CONFIG = {
  baseUrl: "https://firebasestorage.googleapis.com/v0/b",
  bucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  altMedia: "?alt=media",
};
